/**
 * Cosmetic Damage Exclusion rules shared by the offer loader, the offer store and the risk forms
 * (#35739, #35870).
 *
 * The exclusion is modelled as an umbrella flag plus three surfacing-type flags. Several rules
 * re-apply it on load (FO-6 structures always; every risk on a brand that auto-excludes, i.e. CFM;
 * every ACV-settled risk on such a brand). A CFM underwriter (CanApproveUWReview) may override the
 * exclusion on a risk: the persisted `cosmeticDamageUwOverride` marker MEANS "Cosmetic Damage
 * Included" — checked ⇒ the exclusion and all three surfacing flags are off; unchecked ⇒ they are all
 * back on and the brand rules apply again (#35870). While the marker is set none of the rules touch
 * the risk, so it survives reloads, endorsements and renewal. Migration sets the same marker when the
 * legacy policy carried Cosmetic Damage "Included".
 *
 * The override applies to RC building / RC dwelling loss settlements only: an ACV building, dwelling or
 * roof settlement blocks it (see isCosmeticDamageUwOverrideBlockedByAcv).
 */

const FO6_STRUCTURES = 'FO6Structures';

/** The four persisted fields, all-or-nothing: the surfacing boxes can't be unchecked one at a time. */
export const cosmeticDamageExclusionFieldValues = (excluded) => ({
  cosmeticDamageExclusion: excluded,
  exteriorDoorsWindowsDamageExclusion: excluded,
  exteriorWallsDamageExclusion: excluded,
  roofsDamageExclusion: excluded
});

/**
 * Everything the UW Override checkbox writes (#35870): the marker plus the four exclusion flags, which
 * are the inverse of the marker.
 */
export const cosmeticDamageUwOverrideFieldValues = (overridden) => ({
  cosmeticDamageUwOverride: overridden,
  ...cosmeticDamageExclusionFieldValues(!overridden)
});

export const isCosmeticDamageUwOverridden = (coverages) => Boolean(coverages?.cosmeticDamageUwOverride);

/**
 * #35870: the override is only offered on RC building (FO-6 `structureLossSettlement`) / RC dwelling
 * (`lossSettlement`) risks whose roof is not settled at ACV. "RC with ACV for wind/hail"
 * (RC_ACV_WIND_HAIL) does NOT block: it is the roof every RC FO-6 structure is forced to (and the
 * FO-2/FO-3 default), and roofless structures (silos, hoop, solar, fencing) have no roof field at all —
 * blocking on it would have made the override unreachable on most farm buildings.
 */
export const isCosmeticDamageUwOverrideBlockedByAcv = (coverages) =>
  coverages?.lossSettlement === 'ACV' ||
  coverages?.structureLossSettlement === 'ACV' ||
  coverages?.roofLossSettlement === 'ACV';

/** Shown in place of the checkbox when the override is blocked, so a hidden control isn't read as a no-op. */
export const COSMETIC_DAMAGE_UW_OVERRIDE_BLOCKED_NOTE =
  'UW Override to include Cosmetic Damage is available only on RC building/dwelling risks without an ACV roof loss settlement.';

/**
 * Initial form values for a risk's cosmetic damage fields (offer provider), mirroring what the risk
 * form effects would otherwise set so the form doesn't mount dirty. An overridden risk is normalized to
 * "Included" (all four off) whatever the stored flags say — the store used to re-force them on after
 * the UW's save (#35870), and that footprint must not print or rate as an exclusion.
 */
export const resolveCosmeticDamageExclusionInitialValues = (risk, { shouldAutoExcludeCosmeticDamage }) => {
  const coverages = risk?.coverages || {};
  if (isCosmeticDamageUwOverridden(coverages)) {
    return cosmeticDamageExclusionFieldValues(false);
  }
  const stored = {
    cosmeticDamageExclusion: coverages.cosmeticDamageExclusion,
    exteriorDoorsWindowsDamageExclusion: coverages.exteriorDoorsWindowsDamageExclusion,
    exteriorWallsDamageExclusion: coverages.exteriorWallsDamageExclusion,
    roofsDamageExclusion: coverages.roofsDamageExclusion
  };
  const acv = coverages.roofLossSettlement === 'ACV' || coverages.lossSettlement === 'ACV';
  const forced =
    risk?.type === FO6_STRUCTURES || shouldAutoExcludeCosmeticDamage || (shouldAutoExcludeCosmeticDamage && acv);
  return forced ? cosmeticDamageExclusionFieldValues(true) : stored;
};

const EXCLUSION_FIELDS = Object.keys(cosmeticDamageExclusionFieldValues(true));

/**
 * Re-applies the cosmetic damage rules to every farm risk of an offer the store just received from the
 * server, so the form's initial values agree with what the loader/risk forms would set (no spurious
 * dirty state). Rules: every FO-6 structure is excluded; every risk whose brand config routes the
 * section to the auto-excluding component (CFM, `base/cosmetic-damage-exclusion-section`) is excluded.
 * An underwriter-overridden risk (#35870) is the
 * inverse: it is normalized to Included — this is the forcing site #35764 missed, and it re-excluded
 * the UW's choice on every save.
 *
 * `appConfig` is the brand config (passed in so the rule is testable without the store).
 */
export const applyCosmeticDamageRulesToOffer = (offer, appConfig) => {
  if (!offer?.quote?.farmLocations || !Array.isArray(offer.quote.farmLocations)) {
    return offer;
  }

  const withFlags = (risk, excluded) => {
    const target = cosmeticDamageExclusionFieldValues(excluded);
    const current = risk.coverages || {};
    if (EXCLUSION_FIELDS.every((field) => Boolean(current[field]) === excluded)) {
      return risk;
    }
    return { ...risk, coverages: { ...current, ...target } };
  };

  const updatedFarmLocations = offer.quote.farmLocations.map((location) => ({
    ...location,
    risks: location.risks?.map((risk) => {
      if (isCosmeticDamageUwOverridden(risk.coverages)) {
        return withFlags(risk, false);
      }

      // Default to IL when the location has no state (matches the pre-#35870 store behavior)
      const state = location.address?.state || 'IL';
      const autoExcludingBrand =
        appConfig?.farm?.[state]?.risks?.[risk.type]?.customFormComponentPaths?.cosmeticDamageExclusionSection ===
        'base/cosmetic-damage-exclusion-section';
      // (The pre-#35870 store also forced ACV-settled risks on such a brand — a subset of the brand rule.)
      if (risk.type === FO6_STRUCTURES || autoExcludingBrand) {
        return withFlags(risk, true);
      }
      return risk;
    })
  }));

  return { ...offer, quote: { ...offer.quote, farmLocations: updatedFarmLocations } };
};
